import React, { useEffect, useState } from 'react';
import {
  Card,
  Col,
  Empty,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Typography
} from 'antd';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import api from '../../api/axiosConfig';
import {
  CATEGORY_OPTIONS,
  CHART_COLORS,
  TIME_RANGE_OPTIONS,
  formatDateTime,
  formatNumber
} from './analyticsConfig';

const { Title, Text } = Typography;

const ResourceAnalytics = () => {
  const [days, setDays] = useState(30);
  const [category, setCategory] = useState('all');
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [schools, setSchools] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await api.get('/admin/schools');
        setSchools(response.data || []);
      } catch (error) {
        console.error('Failed to load schools for resource analytics:', error);
      }
    };

    fetchSchools();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const response = await api.get('/admin/analytics/resource-insights', {
          params: {
            days,
            category,
            school_id: selectedSchool
          }
        });
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to load resource analytics:', error);
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [category, days, selectedSchool]);

  const schoolOptions = [
    { label: 'All Schools', value: 'all' },
    ...schools.map((school) => ({
      label: school.school_name,
      value: school.school_id
    }))
  ];

  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Resources',
      dataIndex: 'resource_count',
      key: 'resource_count',
      render: formatNumber
    },
    {
      title: 'Downloads',
      dataIndex: 'downloads',
      key: 'downloads',
      render: formatNumber
    },
    {
      title: 'Previews',
      dataIndex: 'previews',
      key: 'previews',
      render: formatNumber
    },
    {
      title: 'School Uploads',
      dataIndex: 'school_uploads',
      key: 'school_uploads',
      render: formatNumber
    }
  ];

  const topResourceColumns = [
    {
      title: 'Resource',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Downloads',
      dataIndex: 'downloads',
      key: 'downloads',
      render: formatNumber
    },
    {
      title: 'Previews',
      dataIndex: 'previews',
      key: 'previews',
      render: formatNumber
    },
    {
      title: 'Unique Schools',
      dataIndex: 'unique_schools',
      key: 'unique_schools',
      render: formatNumber
    },
    {
      title: 'Last Download',
      dataIndex: 'last_downloaded_at',
      key: 'last_downloaded_at',
      render: formatDateTime
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!analytics) {
    return <Empty description="Unable to load resource analytics" />;
  }

  const summary = analytics.summary || {};

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Resource Analytics</Title>
          <Text type="secondary">
            Measure which resources schools preview, download, and contribute the most across categories.
          </Text>
        </div>
        <Space wrap>
          <Select value={days} options={TIME_RANGE_OPTIONS} onChange={setDays} style={{ width: 150 }} />
          <Select value={category} options={CATEGORY_OPTIONS} onChange={setCategory} style={{ width: 170 }} />
          <Select
            value={selectedSchool}
            options={schoolOptions}
            onChange={setSelectedSchool}
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
          />
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card><Statistic title="Resources in Scope" value={summary.total_resources || 0} formatter={formatNumber} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card><Statistic title="Downloads" value={summary.downloads_in_range || 0} formatter={formatNumber} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card><Statistic title="Previews" value={summary.previews_in_range || 0} formatter={formatNumber} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card><Statistic title="School Uploads" value={summary.school_uploads_in_range || 0} formatter={formatNumber} /></Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Resource Engagement Trend">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={analytics.daily_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="downloads" stroke={CHART_COLORS.primary} strokeWidth={2} />
                <Line type="monotone" dataKey="previews" stroke={CHART_COLORS.success} strokeWidth={2} />
                <Line type="monotone" dataKey="uploads" stroke={CHART_COLORS.orange} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Inventory Health">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
              <Statistic title="Pending Approvals" value={summary.pending_approvals || 0} formatter={formatNumber} />
              <Statistic title="Downloading Schools" value={summary.unique_downloading_schools || 0} formatter={formatNumber} />
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Category Performance">
        <Table
          dataSource={analytics.category_summary || []}
          columns={categoryColumns}
          rowKey="category"
          pagination={false}
        />
      </Card>

      <Card title="Top Performing Resources">
        <Table
          dataSource={analytics.top_resources || []}
          columns={topResourceColumns}
          rowKey="resource_id"
          pagination={{ pageSize: 8 }}
          scroll={{ x: 980 }}
        />
      </Card>
    </Space>
  );
};

export default ResourceAnalytics;
